
import React, { createContext, useContext, useState, useEffect } from 'react';
import { AppConfig, ThemeType, AnimationLevel } from '@/types/slimeHabit';

// 定义默认应用配置
const defaultAppConfig: AppConfig = {
  theme: 'system',
  language: 'en',
  notificationsEnabled: true,
  soundEnabled: true,
  animationLevel: 'full'
};

// 创建上下文
interface AppConfigContextType {
  config: AppConfig;
  updateTheme: (theme: ThemeType) => void;
  updateLanguage: (language: string) => void;
  toggleNotifications: () => void;
  toggleSound: () => void;
  updateAnimationLevel: (level: AnimationLevel) => void;
  resetConfig: () => void;
}

const AppConfigContext = createContext<AppConfigContextType | undefined>(undefined);

// 创建提供者组件
export const AppConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<AppConfig>(() => {
    // 从本地存储加载数据（如果有）
    const savedConfig = localStorage.getItem('appConfig');
    return savedConfig ? JSON.parse(savedConfig) : defaultAppConfig;
  });

  // 当配置变化时保存到本地存储
  useEffect(() => {
    localStorage.setItem('appConfig', JSON.stringify(config));
    
    // 应用主题到文档
    if (config.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (config.theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else if (config.theme === 'system') {
      const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }, [config]);

  // 更新主题
  const updateTheme = (theme: ThemeType) => {
    setConfig(prev => ({ ...prev, theme }));
  };

  // 更新语言
  const updateLanguage = (language: string) => {
    setConfig(prev => ({ ...prev, language }));
  };

  // 切换通知
  const toggleNotifications = () => {
    setConfig(prev => ({
      ...prev,
      notificationsEnabled: !prev.notificationsEnabled
    }));
  };

  // 切换声音
  const toggleSound = () => {
    setConfig(prev => ({
      ...prev,
      soundEnabled: !prev.soundEnabled
    }));
  };

  // 更新动画级别
  const updateAnimationLevel = (animationLevel: AnimationLevel) => {
    setConfig(prev => ({ ...prev, animationLevel }));
  };

  // 重置配置
  const resetConfig = () => {
    setConfig(defaultAppConfig);
  };

  return (
    <AppConfigContext.Provider value={{
      config,
      updateTheme,
      updateLanguage,
      toggleNotifications,
      toggleSound,
      updateAnimationLevel,
      resetConfig
    }}>
      {children}
    </AppConfigContext.Provider>
  );
};

// 自定义钩子
export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (context === undefined) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
};
